import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import type { TargetSource } from './systems';

import { PROJECT_SLUG } from '../catalog/component/ComponentPageContext';

export type TargetOption = {
  /** What the request sends: a catalog ref, or the name itself. */
  value: string;
  label: string;
  /** A second line where the label alone does not say it. */
  hint?: string;
  /** The repository, `org/name`, where the target has one. */
  slug?: string;
};

export type TargetsState = {
  loading: boolean;
  error?: boolean;
  options: TargetOption[];
};

const FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.annotations',
  'spec.profile',
];

function labelOf(entity: Entity): string {
  const profile = (entity.spec?.profile ?? {}) as { displayName?: unknown };
  if (typeof profile.displayName === 'string' && profile.displayName) {
    return profile.displayName;
  }
  return entity.metadata.title ?? entity.metadata.name;
}

const byLabel = (a: TargetOption, b: TargetOption) =>
  a.label.localeCompare(b.label);

/**
 * One option per repository (specs/access-request.md AC-4). A repository can
 * hold many components — `platform-systems` holds 21 — and listing each would
 * offer one repository, and one Infisical project, twenty-one times under
 * names that do not say so. So the option is the repository, and it stands for
 * the component named like it, else the first by name: that component's owner
 * is who the backend asks.
 */
function repositoriesOf(components: Entity[]): TargetOption[] {
  const bySlug = new Map<string, Entity[]>();
  for (const entity of components) {
    const slug = entity.metadata.annotations![PROJECT_SLUG];
    bySlug.set(slug, [...(bySlug.get(slug) ?? []), entity]);
  }
  return [...bySlug].map(([slug, held]) => {
    const name = slug.replace(/^.*\//, '');
    const sorted = [...held].sort((a, b) =>
      a.metadata.name.localeCompare(b.metadata.name),
    );
    const standsFor =
      sorted.find(entity => entity.metadata.name === name) ?? sorted[0];
    return {
      value: stringifyEntityRef(standsFor),
      label: slug,
      slug,
      hint:
        held.length === 1
          ? labelOf(held[0])
          : `${held.length} components in the catalog`,
    };
  });
}

/**
 * What a request's target can be picked from (specs/access-request.md AC-4) —
 * always something the catalog holds, so the backend can read its owner, except
 * for a tool seat's fixed list.
 *
 * A platform system the catalog does not hold is still offered, by its name:
 * the request then goes to Developer Experience as having no owner, which is
 * better than a card whose panel offers nothing to pick.
 */
export function useTargets(source: TargetSource): TargetsState {
  const catalogApi = useApi(catalogApiRef);
  const [state, setState] = useState<TargetsState>({
    loading: true,
    options: [],
  });
  const key = JSON.stringify(source);

  useEffect(() => {
    let active = true;
    const parsed = JSON.parse(key) as TargetSource;
    if (parsed.kind === 'list') {
      setState({
        loading: false,
        options: parsed.items.map(item => ({ value: item, label: item })),
      });
      return undefined;
    }
    setState({ loading: true, options: [] });
    (async () => {
      try {
        let options: TargetOption[];
        if (parsed.kind === 'platform') {
          const { items } = await catalogApi.getEntitiesByRefs({
            entityRefs: parsed.refs,
            fields: FIELDS,
          });
          options = parsed.refs.map((ref, index) => {
            const entity = items[index];
            return {
              value: ref,
              label: entity ? labelOf(entity) : ref.replace(/^.*\//, ''),
            };
          });
        } else if (parsed.kind === 'groups') {
          const { items } = await catalogApi.getEntities({
            filter: { kind: 'Group' },
            fields: FIELDS,
          });
          options = items
            .map(entity => ({
              value: stringifyEntityRef(entity),
              label: labelOf(entity),
            }))
            .sort(byLabel);
        } else {
          const { items } = await catalogApi.getEntities({
            filter: { kind: 'Component' },
            fields: FIELDS,
          });
          const withRepository = items.filter(entity =>
            Boolean(entity.metadata.annotations?.[PROJECT_SLUG]),
          );
          options =
            parsed.kind === 'services'
              ? withRepository.map(entity => ({
                  value: stringifyEntityRef(entity),
                  label: labelOf(entity),
                  slug: entity.metadata.annotations?.[PROJECT_SLUG],
                  hint: entity.metadata.annotations?.[PROJECT_SLUG],
                }))
              : repositoriesOf(withRepository);
          options.sort(byLabel);
        }
        if (active) setState({ loading: false, options });
      } catch {
        if (active) setState({ loading: false, error: true, options: [] });
      }
    })();
    return () => {
      active = false;
    };
  }, [catalogApi, key]);

  return state;
}
